/**
 * A pull request page's chat session's transcript as the sidebar's chat shows
 * messages. /code folds the session's saved messages and live events into
 * parts (`buildTimeline`, read through `useCodeSession`); this turns those
 * parts into the sidebar chat's own messages, so its renderers show the session
 * as they showed the sidebar's own chat: each person's message, then one reply
 * holding the agent's text, reasoning and tool calls.
 *
 * The page's tools come out as the sidebar chat's tools they port
 * (`session-tools/pr-page.ts`), with the output that tool gave: the page's
 * actions resolve against what the page shows, as their server tools did
 * (`resolveFocusCodeResult`, `resolveSetPrFileViewedResult`), and cubic's
 * answers are the sidebar tools' own output (`pr-page-tool-call.ts`). Every
 * other step shows as a generic tool row.
 */
import type { SessionPart, TraceStep } from '@/app/(home)/code/_prototype/transcript-model';
import type { AiContext } from '@/components/chat/types/ai-context';
import type { SidebarChatMessage } from '@/components/chat/types/sidebar-chat-message';
import type { StaticToolName } from '@/components/chat/types/tool-types';
import type { CodePromptAttachment } from '@/data/code-sessions/types';
import type { PrPageToolName } from '@/lib/code-sessions/session-tools/pr-page';

import {
  focusCodeSchema,
  resolveFocusCodeResult,
} from '@/app/(chat)/api/chat/chat-tools/focus-code-tool';
import {
  resolveSetPrFileViewedResult,
  setPrFileViewedSchema,
} from '@/app/(chat)/api/chat/chat-tools/set-pr-file-viewed-tool';
import { hasRequiredAddPrCommentInput } from '@/components/chat/tools/apply-add-pr-comment';
import { isStaticTool } from '@/components/chat/types/tool-types';
import { codeModeCallStep } from '@/lib/code-sessions/code-mode-calls';
import {
  DRAFT_PR_COMMENT_TOOL,
  FOCUS_CODE_TOOL,
  PR_PAGE_ACTION_TOOLS,
  PR_PAGE_SIDEBAR_TOOLS,
  SEARCH_CODE_DOCS_TOOL,
  SET_PR_FILE_VIEWED_TOOL,
} from '@/lib/code-sessions/session-tools/pr-page';
import { shownToolStepLabel } from '@/lib/code-sessions/tool-steps';
import { getRecord } from '@/lib/utils/unknown';

type ChatPart = SidebarChatMessage['parts'][number];

/** The page's actions, as the sidebar chat's tools the page acts on (`useCodeSessionChat`). */
export const PAGE_ACTION_TOOL_NAMES: readonly StaticToolName[] = PR_PAGE_ACTION_TOOLS.map(
  (tool) => PR_PAGE_SIDEBAR_TOOLS[tool],
);

/** The sidebar chat tool a page tool's call shows as, when it is one the sidebar types statically. */
function sidebarStaticTool(name: string): StaticToolName | null {
  const sidebarTool: string | null = Object.hasOwn(PR_PAGE_SIDEBAR_TOOLS, name)
    ? PR_PAGE_SIDEBAR_TOOLS[name as PrPageToolName]
    : null;
  return sidebarTool !== null && isStaticTool(sidebarTool) ? sidebarTool : null;
}

/** What the page shows of its files now, which its actions resolve against. */
export type PageFilesContext = Pick<
  AiContext,
  'codeContent' | 'orderedFiles' | 'activeFilename' | 'visibleFilename'
>;

export type SessionChatMessagesInput = {
  parts: readonly SessionPart[];
  context: PageFilesContext;
  /** The session on /code, where what the sidebar does not show opens. */
  sessionUrl: string;
};

export function sessionChatMessages({
  parts,
  context,
  sessionUrl,
}: SessionChatMessagesInput): SidebarChatMessage[] {
  const messages: SidebarChatMessage[] = [];
  let reply: SidebarChatMessage | null = null;
  let promptId = 'session';
  parts.forEach((part, index) => {
    if (part.type === 'user') {
      reply = null;
      // The transcript names a prompt's bubble by the prompt its turn sent (`deterministicPromptId`).
      promptId = part.id ?? `prompt-${index}`;
      messages.push({
        id: promptId,
        role: 'user',
        parts: [
          { type: 'text', text: promptText(part) },
          ...attachmentParts(part.attachments, sessionUrl),
        ],
      });
      return;
    }
    const chatParts = chatPartsOf(part, {
      context,
      sessionUrl,
      key: part.id ?? `${promptId}:${index}`,
    });
    if (chatParts.length === 0) {
      return;
    }
    if (reply === null) {
      reply = { id: `${promptId}:reply`, role: 'assistant', parts: [] };
      messages.push(reply);
    }
    reply.parts.push(...chatParts);
  });
  return messages;
}

/** What a prompt's bubble says: the person's own words, without what the page added. */
function promptText(part: Extract<SessionPart, { type: 'user' }>): string {
  if (part.checkInReason) {
    return `Scheduled check-in: ${part.checkInReason}`;
  }
  const text = part.authoredText ?? part.text;
  return part.messageFrom ? `Message from ${part.messageFrom}:\n\n${text}` : text;
}

function attachmentParts(
  attachments: readonly CodePromptAttachment[] | undefined,
  sessionUrl: string,
): ChatPart[] {
  const parts: ChatPart[] = [];
  for (const attachment of attachments ?? []) {
    if (!attachment.url) {
      continue;
    }
    let url = attachment.url;
    try {
      url = new URL(url, sessionUrl).href;
    } catch {
      // A relative session URL during SSR cannot resolve saved attachment routes.
    }
    parts.push({
      type: 'file',
      filename: attachment.name,
      mediaType: attachment.contentType,
      url,
    });
  }
  return parts;
}

type PartContext = { context: PageFilesContext; sessionUrl: string; key: string };

function chatPartsOf(part: SessionPart, { context, sessionUrl, key }: PartContext): ChatPart[] {
  switch (part.type) {
    case 'text':
      return [{ type: 'text', text: part.markdown, state: 'done' }];
    case 'reasoning':
      return [{ type: 'reasoning', text: part.text, state: part.running ? 'streaming' : 'done' }];
    case 'trace':
      return part.steps.map((step, index) => toolPartOf(step, `${key}:${index}`, context));
    case 'question':
      return [{ type: 'text', text: questionText(part), state: 'done' }];
    case 'boot':
      // The sandbox starting shows above the composer while it starts (`sandbox-start-status.ts`);
      // only a start that failed stays in the transcript.
      return bootFailureParts(part, key);
    case 'result':
      return [{ type: 'text', text: '_Published the session’s work._', state: 'done' }];
    case 'shared_files':
      return [
        {
          type: 'text',
          text: `_Shared ${part.files.length === 1 ? 'a file' : `${part.files.length} files`}: [see ${part.files.length === 1 ? 'it' : 'them'} in cubic](${sessionUrl})._`,
          state: 'done',
        },
      ];
    case 'environment_suggestion':
      if (part.running === true || part.suggestion === null) {
        return [
          {
            type: 'dynamic-tool',
            toolName: 'suggest_environment',
            toolCallId: part.callId,
            title: 'Suggesting an environment',
            input: {},
            state: 'input-available',
          },
        ];
      }
      return [
        {
          type: 'text',
          text: `_Suggested an environment for this repository: [review it in cubic](${sessionUrl})._`,
          state: 'done',
        },
      ];
    case 'findings':
      return [
        {
          type: 'text',
          text: `_Reported ${part.findings.length === 1 ? 'a finding' : `${part.findings.length} findings`}: [see them in cubic](${sessionUrl})._`,
          state: 'done',
        },
      ];
    case 'page':
      if (part.running === true) {
        return [
          {
            type: 'dynamic-tool',
            toolName: 'show_page',
            toolCallId: part.callId,
            title: 'Making a page',
            input: {},
            state: 'input-available',
          },
        ];
      }
      return [
        { type: 'text', text: `_Made a page: [see it in cubic](${sessionUrl})._`, state: 'done' },
      ];
    case 'error':
      return [{ type: 'text', text: `**Something went wrong:** ${part.message}`, state: 'done' }];
    case 'status':
      // The chat's own "Thinking…" covers the agent working between steps.
      return [];
    case 'user':
      return [];
  }
}

/** The agent's question, answered by the person's next message. */
function questionText(part: Extract<SessionPart, { type: 'question' }>): string {
  const questions =
    part.fields.length > 1
      ? part.fields.map((field) => `- ${field.prompt}${optionsText(field.options)}`).join('\n')
      : `${part.prompt}${optionsText(part.fields[0]?.options ?? part.options)}`;
  return `**cubic is asking:** ${questions}\n\n_Reply below to answer._`;
}

function optionsText(options: readonly string[]): string {
  return options.length === 0 ? '' : ` (${options.join(' / ')})`;
}

function bootFailureParts(part: Extract<SessionPart, { type: 'boot' }>, key: string): ChatPart[] {
  const failed = part.phases.find((phase) => phase.status === 'failed');
  if (!failed) {
    return [];
  }
  return [
    {
      type: 'dynamic-tool',
      toolName: 'sandbox',
      toolCallId: `boot:${key}`,
      input: {},
      title: 'Could not start the sandbox',
      state: 'output-error',
      errorText: failed.detail ?? `${failed.label} failed.`,
    },
  ];
}

/** A step as the sidebar chat's tool part: a page tool as the tool it ports, anything else generically. */
function toolPartOf(step: TraceStep, fallbackId: string, context: PageFilesContext): ChatPart {
  const { call } = step;
  if (call !== undefined) {
    const ported = portedToolPart(step, call, context);
    if (ported !== null) {
      return ported;
    }
  }
  const toolCallId = call?.id ?? fallbackId;
  // This chat uses one generic row per script, so keep its children's names in the title.
  const children = step.codeMode?.calls ?? [];
  const title =
    children.length > 0
      ? children
          .map((child) => {
            const childStep = codeModeCallStep(child, step);
            const text = [shownToolStepLabel(childStep), childStep.detail]
              .filter(Boolean)
              .join(' ');
            return childStep.failed ? `${text} (failed)` : text;
          })
          .join('; ')
      : [shownToolStepLabel(step), step.detail].filter(Boolean).join(' ');
  const base = {
    type: 'dynamic-tool',
    toolName: call?.name ?? step.kind,
    toolCallId,
    title,
    input:
      step.codeMode === undefined
        ? { detail: step.detail }
        : { code: step.codeMode.code, toolCalls: step.codeMode.calls },
  } as const;
  if (step.running) {
    return { ...base, state: 'input-available' };
  }
  if (step.failed || step.interrupted) {
    return {
      ...base,
      state: 'output-error',
      errorText: step.interrupted ? 'Stopped.' : (step.error ?? 'Failed.'),
    };
  }
  return { ...base, state: 'output-available', output: step.output ?? step.error ?? step.detail };
}

/**
 * A page tool's step as the sidebar chat's tool it ports, with that tool's
 * output; null when the call does not read as one, which then shows generically.
 */
function portedToolPart(
  step: TraceStep,
  call: NonNullable<TraceStep['call']>,
  context: PageFilesContext,
): ChatPart | null {
  const toolCallId = call.id;
  const failure =
    step.failed || step.interrupted
      ? step.interrupted
        ? 'Stopped.'
        : (step.error ?? 'Failed.')
      : null;

  switch (call.name) {
    case FOCUS_CODE_TOOL: {
      const input = focusCodeSchema.safeParse(call.input);
      if (!input.success) {
        return null;
      }
      return withState('focusCode', toolCallId, input.data, step, failure, () =>
        resolveFocusCodeResult(context, input.data),
      );
    }
    case DRAFT_PR_COMMENT_TOOL: {
      const fields = getRecord(call.input);
      // A line comment, as the sidebar's comment tool's calls are.
      const input = fields === null ? null : { subject_type: 'line', ...fields };
      if (!hasRequiredAddPrCommentInput(input)) {
        return null;
      }
      return withState('addPrComment', toolCallId, input, step, failure, () => ({
        response: input,
      }));
    }
    case SET_PR_FILE_VIEWED_TOOL: {
      const input = setPrFileViewedSchema.safeParse(call.input);
      if (!input.success) {
        return null;
      }
      return withState('setPrFileViewed', toolCallId, input.data, step, failure, () =>
        resolveSetPrFileViewedResult(context, input.data),
      );
    }
    case SEARCH_CODE_DOCS_TOOL: {
      const base = {
        type: 'dynamic-tool',
        toolName: PR_PAGE_SIDEBAR_TOOLS[SEARCH_CODE_DOCS_TOOL],
        toolCallId,
        input: call.input ?? {},
      } as const;
      if (step.running) {
        return { ...base, state: 'input-available' };
      }
      return failure === null
        ? { ...base, state: 'output-available', output: call.output ?? '' }
        : { ...base, state: 'output-error', errorText: failure };
    }
    default: {
      // The review plan has no sidebar tool, and shows as a generic step.
      const answeredAs = sidebarStaticTool(call.name);
      if (answeredAs === null) {
        return null;
      }
      const output = parsedOutput(call.output);
      if (!step.running && failure === null && output === undefined) {
        return null;
      }
      return withState(answeredAs, toolCallId, call.input ?? {}, step, failure, () => output);
    }
  }
}

/**
 * A ported tool part in the state its step is in: running with its input, failed with why, or
 * done with the sidebar tool's output.
 */
function withState(
  name: StaticToolName,
  toolCallId: string,
  input: unknown,
  step: TraceStep,
  failure: string | null,
  output: () => unknown,
): ChatPart {
  const base = { type: `tool-${name}`, toolCallId, input };
  const part = step.running
    ? { ...base, state: 'input-available' }
    : failure !== null
      ? { ...base, state: 'output-error', errorText: failure }
      : { ...base, state: 'output-available', output: output() };
  // The sidebar's tool types pair each name with its input and output, which the calls above
  // read through that tool's own schema and resolver.
  return part as ChatPart;
}

/** A live tool's answer as the object its sidebar tool returned; undefined for text. */
function parsedOutput(output: string | undefined): unknown {
  if (output === undefined) {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(output);
    return typeof parsed === 'object' && parsed !== null ? parsed : undefined;
  } catch {
    return undefined;
  }
}
